/**
 * Where people stop, and how long they stayed before stopping.
 *
 * The three averages are side by side and never merged into one, because they measure different
 * things (R15): leaving a step after 8 seconds is "não entendi", leaving it after 2 min 14 is
 * "tentei e desisti". A single "tempo médio" column would average those two into a number that
 * describes neither.
 *
 * "Abandonaram" covers everything that was not sent — the ones still in progress and the ones who
 * dismissed it — so that *todos* is exactly *concluíram ∪ abandonaram* and nothing falls between
 * the columns.
 *
 * **The two count columns count PEOPLE, and their headers say so** (R22b). "Chegaram" reads as
 * openings otherwise, which is the one thing it is not: one response per person per version (R33),
 * so somebody who closed the modal and came back is one person on this line, not two.
 *
 * @module Pages/BUs/Surveys
 */

import { css } from '@emotion/css';
import type { JSX } from 'react';
import type { SurveyFunnelStep } from '../../../Types/Surveys.types';
import {
  formatCount,
  formatDuration,
  formatPercent,
  funnelShare,
} from '../../../Utils/surveyFormat';

const wrapStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-s);
`;

const scrollStyle = css`
  overflow-x: auto;
`;

const tableStyle = css`
  width: 100%;
  border-collapse: collapse;
  font-size: var(--font-size-sm);
  min-width: 720px;
`;

const headStyle = css`
  text-align: left;
  padding: var(--space-s) var(--space-m);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-secondary);
  border-bottom: 1px solid var(--color-border);
  white-space: nowrap;
`;

const numericHeadStyle = css`
  text-align: right;
`;

const cellStyle = css`
  padding: var(--space-s) var(--space-m);
  border-bottom: 1px solid var(--color-divider);
  white-space: nowrap;
`;

const numericCellStyle = css`
  text-align: right;
  font-variant-numeric: tabular-nums;
`;

const reachedCellStyle = css`
  min-width: 220px;
`;

const barTrackStyle = css`
  position: relative;
  height: 22px;
  border-radius: var(--radius-m);
  background: var(--color-surface-sunken);
  overflow: hidden;
`;

const barFillStyle = css`
  position: absolute;
  inset: 0 auto 0 0;
  background: var(--color-primary-subtle);
  border-right: 2px solid var(--color-primary);
`;

const barTextStyle = css`
  position: relative;
  display: flex;
  align-items: center;
  height: 100%;
  padding: 0 var(--space-s);
  font-size: var(--font-size-2xs);
  font-variant-numeric: tabular-nums;
`;

const droppedStyle = css`
  color: var(--color-danger);
`;

const noteStyle = css`
  margin: 0;
  font-size: var(--font-size-2xs);
  color: var(--color-text-muted);
  text-wrap: pretty;
`;

const emptyStyle = css`
  margin: 0;
  font-size: var(--font-size-sm);
  color: var(--color-text-muted);
`;

export interface ResultsFunnelProps {
  funnel: SurveyFunnelStep[];
  id?: string;
}

export const ResultsFunnel = ({ funnel, id }: ResultsFunnelProps): JSX.Element => {
  if (funnel.length === 0) {
    return (
      <p id={id} className={emptyStyle}>
        Ninguém abriu esta versão ainda, então não há funil para mostrar.
      </p>
    );
  }

  const first = funnel[0].reached;

  return (
    <div className={wrapStyle}>
      <div className={scrollStyle}>
        <table id={id} className={tableStyle}>
          <thead>
            <tr>
              <th className={headStyle}>Passo</th>
              <th className={headStyle}>Pessoas que chegaram</th>
              <th className={`${headStyle} ${numericHeadStyle}`}>Pessoas que saíram aqui</th>
              <th className={`${headStyle} ${numericHeadStyle}`}>Tempo · todos</th>
              <th className={`${headStyle} ${numericHeadStyle}`}>Tempo · concluíram</th>
              <th className={`${headStyle} ${numericHeadStyle}`}>Tempo · abandonaram</th>
            </tr>
          </thead>
          <tbody>
            {funnel.map(step => (
              <tr key={step.step} id={`survey-funnel-step-${step.step}`}>
                <td className={cellStyle}>Passo {step.step}</td>
                <td className={`${cellStyle} ${reachedCellStyle}`}>
                  <div className={barTrackStyle}>
                    <div
                      className={barFillStyle}
                      style={{ width: `${Math.round(funnelShare(step.reached, first) * 100)}%` }}
                    />
                    <span className={barTextStyle}>
                      {formatCount(step.reached)} ·{' '}
                      {formatPercent(funnelShare(step.reached, first))}
                    </span>
                  </div>
                </td>
                <td className={`${cellStyle} ${numericCellStyle} ${droppedStyle}`}>
                  {formatCount(step.dropped)}
                </td>
                <td className={`${cellStyle} ${numericCellStyle}`}>
                  {formatDuration(step.avgMsAll)}
                </td>
                <td className={`${cellStyle} ${numericCellStyle}`}>
                  {formatDuration(step.avgMsCompleted)}
                </td>
                <td className={`${cellStyle} ${numericCellStyle}`}>
                  {formatDuration(step.avgMsAbandoned)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className={noteStyle}>
        "Abandonaram" reúne quem ficou pelo caminho e quem fechou a pesquisa — tudo que não foi
        enviado. No último passo, "pessoas que saíram aqui" conta quem chegou lá e mesmo assim não
        enviou.
      </p>
    </div>
  );
};
